import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { Link } from "react-router-dom";
import { Heart, Trash2, ShoppingBag, Package } from "lucide-react";

const Wishlist = () => {
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWishlist();
    }, []);

    const fetchWishlist = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/wishlist`, {
                headers: { Authorization: token },
            });

            if (!response.ok) throw new Error("Failed to fetch wishlist");

            const data = await response.json();
            setWishlistItems(data);
        } catch (err) {
            setError("ไม่สามารถโหลดรายการถูกใจได้");
        } finally {
            setLoading(false);
        }
    };

    const removeFromWishlist = async (productId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/wishlist/${productId}`, {
                method: "DELETE",
                headers: { Authorization: token },
            });

            if (response.ok) {
                setWishlistItems(wishlistItems.filter(item => item.productId !== productId));
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาด");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-red-600 rounded-lg">
                        <Heart className="w-8 h-8 text-white fill-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">รายการถูกใจ</h1>
                        <p className="text-gray-500">สินค้าที่คุณบันทึกไว้</p>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {wishlistItems.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">ยังไม่มีสินค้าที่ถูกใจ</h3>
                        <p className="text-gray-500 mb-6">เริ่มต้นเพิ่มสินค้าที่คุณชอบลงในรายการถูกใจ</p>
                        <Link
                            to="/all-products"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                        >
                            <ShoppingBag size={18} />
                            ดูสินค้าทั้งหมด
                        </Link>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {wishlistItems.map((item) => (
                            <div
                                key={item.wishlistId}
                                className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
                            >
                                {/* Product Image */}
                                <Link
                                    to={`/product-details/${item.productId}`}
                                    className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0"
                                >
                                    <img
                                        src={item.imageUrl ? `${API_BASE_URL}${item.imageUrl}` : ""}
                                        alt={item.productName}
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </Link>

                                {/* Product Info */}
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/product-details/${item.productId}`}
                                        className="font-semibold text-gray-900 hover:text-red-600 transition-colors line-clamp-1"
                                    >
                                        {item.productName}
                                    </Link>
                                    <p className="text-lg font-bold text-red-600 mt-1">
                                        ฿{item.price?.toLocaleString()}
                                    </p>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2">
                                    <Link
                                        to={`/product-details/${item.productId}`}
                                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                        ดูสินค้า
                                    </Link>
                                    <button
                                        onClick={() => removeFromWishlist(item.productId)}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="ลบออกจากรายการถูกใจ"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Wishlist;
